import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { scrollToTarget } from "@/App";
import { EASE } from "@/components/Reveal";

const LINKS = [
  { label: "Manifesto", href: "#manifesto", id: "nav-link-manifesto" },
  { label: "Chi sono", href: "#chi-siamo", id: "nav-link-chisiamo" },
  { label: "Servizi", href: "#servizi", id: "nav-link-servizi" },
  { label: "Approccio", href: "#approccio", id: "nav-link-approccio" },
  { label: "Contatti", href: "#contatti", id: "nav-link-contatti" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    setTimeout(() => scrollToTarget(href), open ? 250 : 0);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-obsidian/80 backdrop-blur-xl border-b border-white/10"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 h-[72px] flex items-center justify-between">
          <button
            data-testid="nav-brand-logo"
            onClick={() => go(0)}
            className="group shrink-0"
            aria-label="Giango — torna all'inizio"
          >
            <img
              src="/images/logo.jpg"
              alt="Giango — creatività applicata"
              className="h-11 w-auto rounded-lg border border-white/10 group-hover:border-ember/60 transition-colors duration-300"
            />
          </button>

          <nav className="hidden md:flex items-center gap-9">
            {LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={l.id}
                onClick={() => go(l.href)}
                className="text-sm text-zinc-400 hover:text-ink transition-colors duration-300 tracking-wide"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-cta-button"
              onClick={() => go("#contatti")}
              className="group inline-flex items-center gap-2 bg-ember text-obsidian font-semibold text-sm px-5 py-2.5 rounded-full hover:bg-ember-light transition-colors duration-300"
            >
              Richiedi un progetto
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden text-ink p-2"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Chiudi menu" : "Apri menu"}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-obsidian/95 backdrop-blur-2xl md:hidden flex flex-col justify-center px-8"
          >
            <nav className="flex flex-col gap-2">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5, ease: EASE }}
                  onClick={() => go(l.href)}
                  className="text-left font-display font-bold text-4xl text-ink py-3 border-b border-white/10 hover:text-ember transition-colors"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, x: -24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.35, duration: 0.5, ease: EASE }}
                onClick={() => go("#contatti")}
                className="mt-8 inline-flex items-center justify-center gap-2 bg-ember text-obsidian font-semibold px-6 py-4 rounded-full"
              >
                Richiedi un progetto
                <ArrowUpRight className="h-5 w-5" />
              </motion.button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
